import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import A2BPaymentForm from '@/components/a2b/A2BPaymentForm';

const emptyForm = {
  fullName: '',
  line1: '',
  line2: '',
  city: '',
  postcode: '',
  phone: '',
};

export default function StoreCheckoutModal({ product, isOpen, onClose, surfaceClass = 'bg-gray-800' }) {
  const { user, profile } = useAuth();
  const { format } = useCurrency();
  const [step, setStep] = useState('details');
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (isOpen) {
      setStep('details');
      setForm({
        ...emptyForm,
        fullName:
          profile?.full_name ||
          profile?.artist_name ||
          user?.displayName ||
          '',
      });
    }
  }, [isOpen, profile?.full_name, profile?.artist_name, user?.displayName]);

  if (!isOpen || !product || !user?.uid) return null;

  const email = (user?.email || profile?.email || '').trim();
  const price = parseFloat(product.price) || 0;

  const update = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const requiredOk =
    form.fullName.trim() &&
    form.line1.trim() &&
    form.city.trim() &&
    form.postcode.trim() &&
    form.phone.trim() &&
    email;

  const handleDetailsSubmit = (e) => {
    e.preventDefault();
    if (!requiredOk) return;
    setStep('pay');
  };

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[100010] flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="store-checkout-title"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className={`relative my-8 w-full max-w-lg rounded-2xl border border-gray-600 ${surfaceClass}`}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 z-10 rounded-full bg-gray-700 p-2 text-gray-300 transition-colors hover:bg-gray-600 hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="p-6 pt-14">
            <h2 id="store-checkout-title" className="mb-1 text-xl font-bold text-white">
              Checkout — {product.name}
            </h2>
            <p className="mb-6 text-sm text-gray-400">
              Total:{' '}
              <span className="font-semibold text-blue-200">{format(price)}</span>
            </p>

            {step === 'details' ? (
              <form onSubmit={handleDetailsSubmit} className="space-y-4">
                <p className="text-xs text-amber-100/80">
                  Fields marked <span className="font-semibold text-amber-50">required</span> must be completed before payment. Address line 2 is optional.
                </p>
                <div>
                  <Label htmlFor="store-co-full" className="text-gray-300">
                    Full name <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="store-co-full"
                    value={form.fullName}
                    onChange={(e) => update('fullName', e.target.value)}
                    className="mt-1 border-gray-600 bg-gray-900 text-white"
                    required
                    autoComplete="name"
                  />
                </div>
                <div>
                  <Label htmlFor="store-co-email" className="text-gray-300">
                    Email <span className="text-red-400">*</span>{' '}
                    <span className="font-normal text-gray-500">(from your account)</span>
                  </Label>
                  <Input
                    id="store-co-email"
                    value={email}
                    readOnly
                    className="mt-1 border-gray-600 bg-gray-900/70 text-gray-400"
                  />
                </div>
                <div>
                  <Label htmlFor="store-co-l1" className="text-gray-300">
                    Address line 1 <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="store-co-l1"
                    value={form.line1}
                    onChange={(e) => update('line1', e.target.value)}
                    className="mt-1 border-gray-600 bg-gray-900 text-white"
                    required
                    autoComplete="address-line1"
                  />
                </div>
                <div>
                  <Label htmlFor="store-co-l2" className="text-gray-300">
                    Address line 2 <span className="text-gray-500 text-sm font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="store-co-l2"
                    value={form.line2}
                    onChange={(e) => update('line2', e.target.value)}
                    placeholder="Flat, building, etc. (optional)"
                    className="mt-1 border-gray-600 bg-gray-900 text-white"
                    autoComplete="address-line2"
                  />
                </div>
                <div>
                  <Label htmlFor="store-co-city" className="text-gray-300">
                    Town / city <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="store-co-city"
                    value={form.city}
                    onChange={(e) => update('city', e.target.value)}
                    className="mt-1 border-gray-600 bg-gray-900 text-white"
                    required
                    autoComplete="address-level2"
                  />
                </div>
                <div>
                  <Label htmlFor="store-co-post" className="text-gray-300">
                    Postcode <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="store-co-post"
                    value={form.postcode}
                    onChange={(e) => update('postcode', e.target.value)}
                    className="mt-1 border-gray-600 bg-gray-900 text-white"
                    required
                    autoComplete="postal-code"
                  />
                </div>
                <div>
                  <Label htmlFor="store-co-phone" className="text-gray-300">
                    Telephone <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="store-co-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    className="mt-1 border-gray-600 bg-gray-900 text-white"
                    required
                    autoComplete="tel"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-500"
                    disabled={!requiredOk}
                  >
                    Continue to payment
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="ghost"
                  className="mb-2 text-gray-400 hover:text-white"
                  onClick={() => setStep('details')}
                >
                  ← Edit details
                </Button>
                <A2BPaymentForm
                  amount={price}
                  artistId={user.uid}
                  currency="GBP"
                  mode="store"
                  returnPath="/admin/store"
                  description={`YSR Store: ${product.name}`}
                  requestData={{
                    orderReference: `STORE-${product.id}-${Date.now()}`,
                    storeProductId: product.id,
                    storeProductName: product.name,
                    shippingFullName: form.fullName.trim(),
                    shippingLine1: form.line1.trim(),
                    shippingLine2: (form.line2 || '').trim(),
                    shippingCity: form.city.trim(),
                    shippingPostcode: form.postcode.trim(),
                    shippingPhone: form.phone.trim(),
                    buyerEmail: email,
                  }}
                  onCancel={() => setStep('details')}
                />
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
